import {
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  StreamConfirmationPartTypeName,
  TerraUIMessageDataPartName,
  type BackgroundExecutionKind,
  type HITLRequestConfirmationMessage,
  type TerraUIMessage,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import {
  subagentOwnedPartIndexes,
  type ParallelAgentTerminalStatus,
} from '@terra/ui-design-system';
import { create } from 'zustand';

export type PendingBackgroundExecution = {
  copilotThreadId: string;
  agentThreadId: string;
  toolCallId: string;
  applicationId: string;
  surfaceId?: string;
  targetType?: string;
  agentType: string;
  kind: BackgroundExecutionKind;
  title?: string;
  waitingInput?: boolean;
  pausedOnParentTurn?: boolean;
  terminalStatus?: ParallelAgentTerminalStatus;
  trackedAt?: number;
};

type PendingBackgroundExecutionInput = Omit<
  PendingBackgroundExecution,
  'kind'
> & {
  kind?: BackgroundExecutionKind;
};

type BackgroundExecutionState = {
  resumeSuppressedChatIds: Set<string>;
  pendingExecutionsByToolCallId: Map<string, PendingBackgroundExecution>;
  pendingRevision: number;
  composerLoadingSuppressedChatIds: Set<string>;

  suppressResume: (chatId: string) => void;
  isResumeSuppressed: (chatId: string) => boolean;
  clearResumeSuppression: (chatId: string) => void;

  setPendingExecution: (execution: PendingBackgroundExecutionInput) => void;
  getPendingExecution: (
    toolCallId: string,
  ) => PendingBackgroundExecution | undefined;
  getPendingExecutionByAgentThread: (
    agentThreadId: string,
  ) => PendingBackgroundExecution | undefined;
  markSubagentTerminal: ({
    agentThreadId,
    toolCallId,
    terminalStatus,
  }: {
    agentThreadId?: string;
    toolCallId?: string;
    terminalStatus: NonNullable<PendingBackgroundExecution['terminalStatus']>;
  }) => void;
  clearPendingExecutionsForAgentThread: ({
    agentThreadId,
  }: {
    agentThreadId: string;
  }) => void;
  clearTerminalSubagentsForThread: ({
    copilotThreadId,
  }: {
    copilotThreadId: string;
  }) => void;

  suppressComposerLoading: (chatId: string) => void;
  clearComposerLoadingSuppression: (chatId: string) => void;
};

export function countWorkingSubagents({
  executions,
  chatId,
}: {
  executions: Iterable<PendingBackgroundExecution>;
  chatId: string;
}): number {
  return [...executions].filter(
    (execution) =>
      execution.copilotThreadId === chatId &&
      execution.kind === 'subagent' &&
      !execution.waitingInput &&
      !execution.terminalStatus,
  ).length;
}

export function hasComposerLoadingBackgroundExecution({
  executions,
  chatId,
}: {
  executions: Iterable<PendingBackgroundExecution>;
  chatId: string;
}): boolean {
  return [...executions].some(
    (execution) =>
      execution.copilotThreadId === chatId && execution.kind !== 'subagent',
  );
}

export function shouldResumeOnBackgroundCompleted({
  kind,
  isResumeSuppressed,
}: {
  kind: BackgroundExecutionKind;
  isResumeSuppressed: boolean;
}): boolean {
  return kind !== 'subagent' && !isResumeSuppressed;
}

function isToolProgressPart(
  part: NonNullable<TerraUIMessage['parts']>[number],
): part is {
  type: typeof TerraUIMessageDataPartName;
  id?: string;
  data: ToolCallGenericMessage;
} {
  if (part.type !== TerraUIMessageDataPartName) {
    return false;
  }

  const data = part.data;

  if (!data) {
    return false;
  }

  return (
    data.type === DataMessageTypeEnum.ToolCallUpdate ||
    data.type === DataMessageTypeEnum.ToolCallStart ||
    data.type === DataMessageTypeEnum.ToolCallComplete ||
    data.type === DataMessageTypeEnum.ToolCallError
  );
}

function parentToolCallIdFromProgress(
  progress: ToolCallGenericMessage,
): string | undefined {
  const parentToolCallId = progress.metadata?.parentToolCallId;

  return typeof parentToolCallId === 'string' ? parentToolCallId : undefined;
}

export function patchMessagesByToolCallId({
  messages,
  toolCallId,
  progress,
}: {
  messages: TerraUIMessage[];
  toolCallId: string;
  progress: ToolCallGenericMessage;
}): TerraUIMessage[] {
  let changed = false;
  const next = messages.map((message) => {
    if (!message.parts) {
      return message;
    }

    const parts = message.parts.map((part) => {
      if (!isToolProgressPart(part) || part.data.toolCallId !== toolCallId) {
        return part;
      }

      changed = true;
      const current = part.data;
      const data: ToolCallGenericMessage = { ...current };

      for (const [key, value] of Object.entries(progress)) {
        if (value !== undefined) {
          Reflect.set(data, key, value);
        }
      }

      return {
        ...part,
        data,
      };
    });

    return { ...message, parts };
  });

  if (changed) {
    return next;
  }

  const parentToolCallId = parentToolCallIdFromProgress(progress);

  if (!parentToolCallId) {
    return messages;
  }

  const parentIndex = messages.findIndex((message) =>
    message.parts?.some(
      (part) =>
        isToolProgressPart(part) && part.data.toolCallId === parentToolCallId,
    ),
  );

  if (parentIndex < 0) {
    return messages;
  }

  return messages.map((message, index) => {
    if (index !== parentIndex || !message.parts) {
      return message;
    }

    const part: NonNullable<TerraUIMessage['parts']>[number] =
      progress.metadata?.activity === 'reasoning'
        ? {
            type: 'reasoning',
            text: progress.message,
            providerMetadata: {
              metadata: { parentToolCallId },
            },
          }
        : {
            type: TerraUIMessageDataPartName,
            id: progress.id ?? toolCallId,
            data: progress,
          };
    const parentPartIndex = message.parts.findIndex(
      (candidate) =>
        isToolProgressPart(candidate) &&
        candidate.data.toolCallId === parentToolCallId,
    );
    const insertAt =
      Math.max(
        parentPartIndex,
        ...subagentOwnedPartIndexes({
          parts: message.parts,
          parentToolCallId,
        }),
      ) + 1;

    return {
      ...message,
      parts: [
        ...message.parts.slice(0, insertAt),
        part,
        ...message.parts.slice(insertAt),
      ],
    };
  });
}

export const isBackgroundSubagentHitl = (
  value: unknown,
): value is HITLRequestConfirmationMessage => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    record.type === ConfirmationMessageTypeEnum.HITLRequestConfirmation &&
    Boolean(
      record.backgroundSubagent &&
        typeof record.backgroundSubagent === 'object',
    )
  );
};

const hitlActionIdentityIds = (data: unknown): string[] => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return [];
  }

  const record = data as {
    type?: unknown;
    id?: unknown;
    hitlRequestConfirmationPrompt?: {
      actionRequests?: Array<{ id?: unknown; toolCallId?: unknown }>;
    };
  };

  if (record.type !== ConfirmationMessageTypeEnum.HITLRequestConfirmation) {
    return [];
  }

  return [
    record.id,
    ...(record.hitlRequestConfirmationPrompt?.actionRequests ?? []).flatMap(
      (request) => [request.id, request.toolCallId],
    ),
  ].filter((id): id is string => typeof id === 'string' && id.length > 0);
};

const hitlConfirmationIdentityIds = (
  confirmation: HITLRequestConfirmationMessage,
): string[] =>
  [
    confirmation.id,
    ...(confirmation.hitlRequestConfirmationPrompt?.actionRequests?.flatMap(
      (request) => [request.id, request.toolCallId],
    ) ?? []),
  ].filter((id): id is string => Boolean(id));

function identifyBackgroundHitlConfirmation({
  confirmation,
  parentToolCallId,
}: {
  confirmation: HITLRequestConfirmationMessage;
  parentToolCallId: string;
}): HITLRequestConfirmationMessage {
  if (confirmation.id) {
    return confirmation;
  }

  const actionRequest =
    confirmation.hitlRequestConfirmationPrompt?.actionRequests?.find(
      (request) => request.toolCallId || request.id,
    );
  const id = actionRequest?.toolCallId ?? actionRequest?.id ?? parentToolCallId;

  return { ...confirmation, id };
}

export function appendBackgroundSubagentHitlPart({
  messages,
  toolCallId,
  confirmation,
}: {
  messages: TerraUIMessage[];
  toolCallId: string;
  confirmation: HITLRequestConfirmationMessage;
}): TerraUIMessage[] {
  const identified = identifyBackgroundHitlConfirmation({
    confirmation,
    parentToolCallId: toolCallId,
  });
  const incomingIds = new Set(hitlConfirmationIdentityIds(identified));
  const alreadyPresent = messages.some((message) =>
    message.parts?.some((part) => {
      if (
        part.type !== StreamConfirmationPartTypeName &&
        part.type !== TerraUIMessageDataPartName
      ) {
        return false;
      }

      const actionIds = hitlActionIdentityIds(part.data);

      if (incomingIds.size > 0 && actionIds.some((id) => incomingIds.has(id))) {
        return true;
      }

      if (!isBackgroundSubagentHitl(part.data)) {
        return false;
      }

      if (part.data.backgroundSubagent?.toolCallId !== toolCallId) {
        return false;
      }

      return incomingIds.size === 0;
    }),
  );

  if (alreadyPresent) {
    return messages;
  }

  const ownerIndex = messages.findIndex((message) =>
    message.parts?.some((part) => {
      if (part.type !== TerraUIMessageDataPartName) {
        return false;
      }

      const data = part.data as Record<string, unknown> | undefined;

      return data?.toolCallId === toolCallId;
    }),
  );

  const actionRequest =
    identified.hitlRequestConfirmationPrompt?.actionRequests?.[0];
  const confirmationMessageId =
    actionRequest?.toolCallId ??
    actionRequest?.id ??
    identified.id ??
    toolCallId;

  const isOwnedBackgroundHitlMessage = (message: TerraUIMessage): boolean =>
    Boolean(
      message.parts?.some(
        (part) =>
          (part.type === StreamConfirmationPartTypeName ||
            part.type === TerraUIMessageDataPartName) &&
          isBackgroundSubagentHitl(part.data) &&
          part.data.backgroundSubagent?.toolCallId === toolCallId,
      ),
    );

  let insertIndex = ownerIndex < 0 ? messages.length : ownerIndex + 1;

  while (
    insertIndex < messages.length &&
    isOwnedBackgroundHitlMessage(messages[insertIndex])
  ) {
    insertIndex += 1;
  }

  const confirmationMessage: TerraUIMessage = {
    id: confirmationMessageId,
    role: 'assistant',
    parts: [
      {
        id: confirmationMessageId,
        type: StreamConfirmationPartTypeName,
        data: identified,
      },
    ],
  };

  return [
    ...messages.slice(0, insertIndex),
    confirmationMessage,
    ...messages.slice(insertIndex),
  ];
}

export const useBackgroundExecutionStore = create<BackgroundExecutionState>(
  (set, get) => ({
    resumeSuppressedChatIds: new Set<string>(),
    pendingExecutionsByToolCallId: new Map<
      string,
      PendingBackgroundExecution
    >(),
    pendingRevision: 0,
    composerLoadingSuppressedChatIds: new Set<string>(),

    suppressResume: (chatId) => {
      const next = new Set(get().resumeSuppressedChatIds);

      next.add(chatId);
      set({ resumeSuppressedChatIds: next });
    },

    isResumeSuppressed: (chatId) => get().resumeSuppressedChatIds.has(chatId),

    clearResumeSuppression: (chatId) => {
      const next = new Set(get().resumeSuppressedChatIds);

      next.delete(chatId);
      set({ resumeSuppressedChatIds: next });
    },

    setPendingExecution: (execution) => {
      const next = new Map(get().pendingExecutionsByToolCallId);
      const existing = next.get(execution.toolCallId);

      next.set(execution.toolCallId, {
        ...execution,
        kind: execution.kind ?? existing?.kind ?? 'neo',
        title: execution.title ?? existing?.title,
        surfaceId: execution.surfaceId ?? existing?.surfaceId,
        targetType: execution.targetType ?? existing?.targetType,
        trackedAt: execution.trackedAt ?? existing?.trackedAt ?? Date.now(),
        ...(Object.hasOwn(execution, 'waitingInput')
          ? {}
          : { waitingInput: existing?.waitingInput }),
        ...(Object.hasOwn(execution, 'pausedOnParentTurn')
          ? {}
          : { pausedOnParentTurn: existing?.pausedOnParentTurn }),
        ...(Object.hasOwn(execution, 'terminalStatus')
          ? execution.terminalStatus
            ? { terminalStatus: execution.terminalStatus }
            : {}
          : existing?.terminalStatus
            ? { terminalStatus: existing.terminalStatus }
            : {}),
      });
      set({
        pendingExecutionsByToolCallId: next,
        pendingRevision: get().pendingRevision + 1,
      });
    },

    getPendingExecution: (toolCallId) =>
      get().pendingExecutionsByToolCallId.get(toolCallId),

    getPendingExecutionByAgentThread: (agentThreadId) =>
      [...get().pendingExecutionsByToolCallId.values()].find(
        (execution) => execution.agentThreadId === agentThreadId,
      ),

    markSubagentTerminal: ({ agentThreadId, toolCallId, terminalStatus }) => {
      const current = get().pendingExecutionsByToolCallId;
      let changed = false;
      const next = new Map(current);

      for (const [id, execution] of current.entries()) {
        const matches =
          (agentThreadId !== undefined &&
            execution.agentThreadId === agentThreadId) ||
          (toolCallId !== undefined && execution.toolCallId === toolCallId);
        const completedAfterStop =
          terminalStatus === 'completed' &&
          (execution.terminalStatus === 'aborted' ||
            execution.terminalStatus === 'error');

        if (
          !matches ||
          execution.kind !== 'subagent' ||
          execution.terminalStatus === terminalStatus ||
          completedAfterStop
        ) {
          continue;
        }

        next.set(id, {
          ...execution,
          waitingInput: undefined,
          terminalStatus,
        });
        changed = true;
      }

      if (!changed) {
        return;
      }

      set({
        pendingExecutionsByToolCallId: next,
        pendingRevision: get().pendingRevision + 1,
      });
    },

    clearPendingExecutionsForAgentThread: ({ agentThreadId }) => {
      const current = get().pendingExecutionsByToolCallId;
      let changed = false;
      const next = new Map(current);

      for (const [toolCallId, execution] of current.entries()) {
        if (execution.agentThreadId === agentThreadId) {
          next.delete(toolCallId);
          changed = true;
        }
      }

      if (!changed) {
        return;
      }

      set({
        pendingExecutionsByToolCallId: next,
        pendingRevision: get().pendingRevision + 1,
      });
    },

    clearTerminalSubagentsForThread: ({ copilotThreadId }) => {
      const current = get().pendingExecutionsByToolCallId;
      let changed = false;
      const next = new Map(current);

      for (const [toolCallId, execution] of current.entries()) {
        if (
          execution.copilotThreadId !== copilotThreadId ||
          execution.kind !== 'subagent' ||
          !execution.terminalStatus
        ) {
          continue;
        }

        next.delete(toolCallId);
        changed = true;
      }

      if (!changed) {
        return;
      }

      set({
        pendingExecutionsByToolCallId: next,
        pendingRevision: get().pendingRevision + 1,
      });
    },

    suppressComposerLoading: (chatId) => {
      const next = new Set(get().composerLoadingSuppressedChatIds);

      next.add(chatId);
      set({ composerLoadingSuppressedChatIds: next });
    },

    clearComposerLoadingSuppression: (chatId) => {
      const next = new Set(get().composerLoadingSuppressedChatIds);

      next.delete(chatId);
      set({ composerLoadingSuppressedChatIds: next });
    },
  }),
);
